"use client";

import {
  Box,
  Field,
  Flex,
  Input,
  Popover,
  Portal,
  Text,
  chakra,
} from "@chakra-ui/react";
import type { CountryCode } from "libphonenumber-js";
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LuChevronDown } from "react-icons/lu";

import {
  COUNTRIES,
  type CountryEntry,
} from "@components/common/PhoneInputCountries";
import { formatE164, parsePhone } from "lib/phoneUtils";

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  defaultCountry?: CountryCode;
  size?: "sm" | "md";
  placeholder?: string;
  borderColor?: string;
  onBlur?: () => void;
}

function getCountryByCode(code: CountryCode): CountryEntry {
  return COUNTRIES.find((c) => c.code === code) ?? COUNTRIES[0];
}

interface CountryRowProps {
  entry: CountryEntry;
  selected: boolean;
  onSelect: (entry: CountryEntry) => void;
}

const CountryRow = memo(function CountryRow({
  entry,
  selected,
  onSelect,
}: CountryRowProps) {
  const onClick = useCallback(() => {
    onSelect(entry);
  }, [entry, onSelect]);

  return (
    <Flex
      gap="2"
      align="center"
      px="2"
      py="1"
      fontSize="xs"
      cursor="pointer"
      borderRadius="sm"
      bg={selected ? "bg.muted" : undefined}
      _hover={{ bg: "bg.muted" }}
      onClick={onClick}
    >
      <Text>{entry.flag}</Text>
      <Text flex="1" truncate>
        {entry.name}
      </Text>
      <Text color="fg.muted">{entry.dialCode}</Text>
    </Flex>
  );
});

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  function PhoneInput(
    {
      value,
      onChange,
      defaultCountry = "GH",
      size = "sm",
      placeholder = "Phone number",
      borderColor = "border.light",
      onBlur,
    },
    ref,
  ) {
    const initialParsed = parsePhone(value);

    const [country, setCountry] = useState<CountryCode>(
      initialParsed != null ? initialParsed.country : defaultCountry,
    );

    const [nationalNumber, setNationalNumber] = useState(
      initialParsed != null ? initialParsed.nationalNumber : "",
    );

    const [searchQuery, setSearchQuery] = useState("");
    const [open, setOpen] = useState(false);
    const skipSyncRef = useRef(false);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const selectedCountry = getCountryByCode(country);

    const filteredCountries = useMemo(() => {
      if (searchQuery.trim() === "") {
        return COUNTRIES;
      }
      const q = searchQuery.toLowerCase();
      return COUNTRIES.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.dialCode.includes(q) ||
          c.code.toLowerCase().includes(q),
      );
    }, [searchQuery]);

    const onSelectCountry = useCallback(
      (entry: CountryEntry) => {
        setCountry(entry.code);
        setSearchQuery("");
        setOpen(false);
        skipSyncRef.current = true;
        const e164 = formatE164(entry.code, nationalNumber);
        onChange(e164);
      },
      [nationalNumber, onChange],
    );

    const onNumberChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        setNationalNumber(raw);
        skipSyncRef.current = true;
        const e164 = formatE164(country, raw);
        onChange(e164);
      },
      [country, onChange],
    );

    const onSearchChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
      },
      [],
    );

    const onOpenChange = useCallback((details: { open: boolean }) => {
      setOpen(details.open);
      if (!details.open) {
        setSearchQuery("");
      }
    }, []);

    useEffect(() => {
      if (skipSyncRef.current) {
        skipSyncRef.current = false;
        return;
      }
      const parsed = parsePhone(value);
      if (parsed != null) {
        setCountry(parsed.country);
        setNationalNumber(parsed.nationalNumber);
      } else if (value === "") {
        setNationalNumber("");
      }
    }, [value]);

    useEffect(() => {
      if (!open) {
        return;
      }
      const id = requestAnimationFrame(() => {
        searchInputRef.current?.focus();
      });
      return () => cancelAnimationFrame(id);
    }, [open]);

    return (
      <Flex width="full" align="stretch">
        <Popover.Root
          open={open}
          onOpenChange={onOpenChange}
          positioning={{ placement: "bottom-start" }}
          lazyMount
        >
          <Popover.Trigger asChild>
            <chakra.button
              display="flex"
              alignItems="center"
              gap="1"
              borderWidth="1px"
              borderColor={borderColor}
              borderRightWidth="0"
              borderRightRadius="0"
              borderLeftRadius="md"
              px="2"
              cursor="pointer"
              bg="transparent"
              _hover={{ bg: "bg.muted" }}
              flexShrink={0}
            >
              <Text fontSize={size === "sm" ? "xs" : "sm"} whiteSpace="nowrap">
                {selectedCountry.flag} {selectedCountry.dialCode}
              </Text>
              <LuChevronDown size={12} />
            </chakra.button>
          </Popover.Trigger>
          <Portal>
            <Popover.Positioner>
              <Popover.Content
                width="260px"
                bg="surface.background"
                borderColor="border.muted"
                borderWidth="1px"
                borderRadius="md"
                boxShadow="md"
                p="1"
              >
                <Box p="1">
                  <Field.Root invalid={false}>
                    <Input
                      ref={searchInputRef}
                      placeholder="Search country..."
                      size="sm"
                      borderColor={borderColor}
                      value={searchQuery}
                      onChange={onSearchChange}
                    />
                  </Field.Root>
                </Box>
                <Box maxH="200px" overflowY="auto">
                  {filteredCountries.length === 0 ? (
                    <Text fontSize="xs" color="fg.muted" p="2">
                      No countries found
                    </Text>
                  ) : (
                    filteredCountries.map((c) => (
                      <CountryRow
                        key={c.code}
                        entry={c}
                        selected={c.code === country}
                        onSelect={onSelectCountry}
                      />
                    ))
                  )}
                </Box>
              </Popover.Content>
            </Popover.Positioner>
          </Portal>
        </Popover.Root>
        <Input
          ref={ref}
          type="tel"
          size={size}
          placeholder={placeholder}
          borderColor={borderColor}
          borderLeftRadius="0"
          value={nationalNumber}
          onChange={onNumberChange}
          onBlur={onBlur}
          flex="1"
        />
      </Flex>
    );
  },
);

export default PhoneInput;
